import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { FileText, Edit, Trash2, Eye, Plus } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import DataTable from '../../components/admin/DataTable';
import LoadingState from '../../components/admin/LoadingState';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import toast from 'react-hot-toast';

const Blog: React.FC = () => {
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{isOpen: boolean, post: any}>({
    isOpen: false,
    post: null
  });
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Gestion du Blog | Admin';
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error loading blog posts:', error);
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (post: any) => {
    navigate(`/admin/blog/edit/${post.id}`);
  };

  const handleView = (post: any) => {
    window.open(`/blog/${post.slug}`, '_blank');
  };

  const handleDelete = async () => {
    if (!deleteConfirm.post) return;
    
    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', deleteConfirm.post.id);

      if (error) throw error;
      
      setPosts(posts.filter(p => p.id !== deleteConfirm.post.id));
      toast.success('Article supprimé avec succès');
    } catch (error) {
      console.error('Error deleting blog post:', error);
      toast.error('Erreur lors de la suppression de l\'article');
    } finally {
      setDeleteConfirm({ isOpen: false, post: null });
    }
  };

  const columns = [
    {
      key: 'title',
      header: 'Titre',
      render: (value: string, item: any) => (
        <div className="flex items-center">
          <div className="w-10 h-10 flex-shrink-0 mr-3">
            {item.cover_image ? (
              <img 
                src={item.cover_image} 
                alt={value}
                className="w-10 h-10 rounded-md object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40';
                }}
              />
            ) : (
              <div className="w-10 h-10 rounded-md bg-blue-100 flex items-center justify-center">
                <FileText size={20} className="text-blue-600" />
              </div>
            )}
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-xs text-gray-500">{item.slug}</div>
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'category',
      header: 'Catégorie',
      render: (value: string) => (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
          {value}
        </span>
      ),
      sortable: true,
      width: 'w-32'
    },
    {
      key: 'read_time',
      header: 'Temps de lecture',
      render: (value: number) => `${value} min`,
      sortable: true,
      width: 'w-32'
    },
    {
      key: 'created_at',
      header: 'Publié le',
      render: (value: string) => new Date(value).toLocaleDateString('fr-FR'),
      sortable: true,
      width: 'w-32'
    }
  ];

  return (
    <div className="px-6">
      <PageHeader
        title="Blog"
        description="Gérez les articles de votre blog"
        actionLink="/admin/blog/new"
        actionLabel="Ajouter un article"
        actionIcon={<Plus size={16} />}
      />
      
      {loading ? (
        <LoadingState type="table" />
      ) : (
        <DataTable
          columns={columns}
          data={posts}
          onEdit={handleEdit}
          onDelete={(post) => setDeleteConfirm({ isOpen: true, post })}
          onView={handleView}
          searchPlaceholder="Rechercher un article..."
          searchKey="title"
          emptyTitle="Aucun article"
          emptyDescription="Vous n'avez pas encore publié d'article."
          emptyActionLabel="Ajouter un article"
          onEmptyAction={() => navigate('/admin/blog/new')}
        />
      )}
      
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Supprimer l'article"
        message={`Êtes-vous sûr de vouloir supprimer l'article "${deleteConfirm.post?.title}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, post: null })}
        type="danger"
      />
    </div>
  );
};

export default Blog;